import React, { useEffect, useState } from 'react';
import DataTable from '../../components/common/DataTable/DataTable';
import Button from '../../components/common/Button/Button';
import Input from '../../components/common/Input/Input';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import styles from './NewsFeed.module.css';
import FullscreenModal from '../../components/common/FullscreenModal/FullscreenModal';
import CorporateAnnouncementForm from './CorporateAnnouncementForm';

const EditIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6" />
        <path d="M14 11v6" />
        <path d="M9 6V4h6v2" />
    </svg>
);



const CorporateAnnouncementsTab = () => {
    const navigate = useNavigate();
    const [announcements, setAnnouncements] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [titleFilter, setTitleFilter] = useState('');
    const [subjectFilter, setSubjectFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [selectedAnnouncementId, setSelectedAnnouncementId] = useState(null);
    const limit = 20;

    const fetchAnnouncements = (overrides = {}) => {
        const queryParams = {
            page: overrides.page !== undefined ? overrides.page : page,
            limit,
            title: (overrides.titleFilter !== undefined ? overrides.titleFilter : titleFilter) || undefined,
            subject: (overrides.subjectFilter !== undefined ? overrides.subjectFilter : subjectFilter) || undefined,
            is_published: overrides.statusFilter !== undefined ? overrides.statusFilter : statusFilter,
            date_from: (overrides.dateFrom !== undefined ? overrides.dateFrom : dateFrom) || undefined,
            date_to: (overrides.dateTo !== undefined ? overrides.dateTo : dateTo) || undefined,
        };

        // Remove undefined/empty values
        Object.keys(queryParams).forEach(key => {
            if (queryParams[key] === undefined || queryParams[key] === '') {
                delete queryParams[key];
            }
        });

        api.get('/corporate-announcements/raw', { params: queryParams })
            .then((response) => {
                setAnnouncements(response.data || []);
                setTotalPages(response.totalPages || 1);
            })
            .catch((error) => {
                console.error('Error fetching corporate announcements:', error);
            });
    };

    const handleDelete = (id) => {
        if (!window.confirm("Are you sure you want to delete this announcement?")) return;

        api.delete(`/corporate-announcements/raw/${id}`)
            .then(() => {
                setAnnouncements(prev => prev.filter(a => a.id !== id));
                toast.success("✅ Announcement deleted successfully");
            })
            .catch((error) => {
                console.error("Error deleting announcement:", error);
                toast.error("❌ Failed to delete announcement");
            });
    };

    const handleClearFilters = () => {
        setTitleFilter('');
        setSubjectFilter('');
        setStatusFilter('');
        setDateFrom('');
        setDateTo('');
        setPage(1);
        fetchAnnouncements({
            page: 1,
            titleFilter: '',
            subjectFilter: '',
            statusFilter: '',
            dateFrom: '',
            dateTo: ''
        });
    };

    useEffect(() => {
        fetchAnnouncements();
    }, [page]);

    const columns = [
        { key: 'id', label: 'ID', width: '60px' },
        {
            key: 'title',
            label: 'Company',
            width: '200px',
            render: (value) => (
                <span style={{ fontWeight: 600, color: 'var(--color-primary)' }}>{value || '—'}</span>
            ),
        },
        {
            key: 'subject',
            label: 'Subject',
            width: '240px',
            render: (value) => value ? (
                <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'var(--text-body)',
                    background: 'var(--bg-gray)',
                    border: '1px solid #e5e7eb'
                }}>
                    {value}
                </span>
            ) : '—',
        },
        {
            key: 'description',
            label: 'Description',
            width: '450px',
            render: (value) => value || '—',
        },
        {
            key: 'pub_date',
            label: 'Published Date',
            width: '140px',
            render: (value) => {
                if (!value) return '—';
                const dateObj = new Date(value);
                const datePart = dateObj.toLocaleDateString('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' });
                const timePart = dateObj.toLocaleTimeString('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
                
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ color: 'var(--text-dark)', fontWeight: 600 }}>{datePart}</span>
                        <span style={{ color: '#4b5563' }}>{timePart}</span>
                    </div>
                );
            }
        },
        {
            key: 'is_published',
            label: 'Status',
            width: '90px',
            render: (value) => (
                <span style={{
                    display: 'inline-block',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.5px',
                    color: value ? '#166534' : 'var(--text-muted)',
                    background: value ? '#dcfce7' : 'var(--bg-gray)',
                    border: value ? '1px solid #bbf7d0' : '1px solid #e5e7eb',
                }}>
                    {value ? 'YES' : 'NO'}
                </span>
            ),
        },
        {
            key: 'link',
            label: 'PDF/XBRL',
            width: '90px',
            render: (value) => {
                if (!value) return '—';
                const isXbrl = value.toLowerCase().includes('xbrl') || value.toLowerCase().endsWith('.zip') || value.toLowerCase().endsWith('.xml');
                const iconName = isXbrl ? 'data_object' : 'picture_as_pdf';
                const color = isXbrl ? '#0284c7' : '#dc2626';
                const title = isXbrl ? 'Open XBRL' : 'Open PDF';

                return (
                    <button
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: '3px',
                            padding: '3px 8px', borderRadius: '6px', border: '1px solid #e5e7eb',
                            background: '#fff', color: color, fontSize: '11px', fontWeight: 600,
                            cursor: 'pointer',
                        }}
                        onClick={() => window.open(value, '_blank')}
                        title={title}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>{iconName}</span>
                    </button>
                );
            }
        },
        {
            key: 'actions',
            label: 'Actions',
            width: '100px',
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                    <button
                        className={styles.iconBtn}
                        onClick={() => {
                            setSelectedAnnouncementId(row.id);
                            setIsFormOpen(true);
                        }}
                        title="Publish as News"
                    >
                        <EditIcon />
                    </button>
                    <button
                        className={`${styles.iconBtn} ${styles.deleteBtn}`}
                        onClick={() => handleDelete(row.id)}
                        title="Delete announcement"
                    >
                        <TrashIcon />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className={styles.stickyHeader}>
                <div className={styles.headerTop}>
                    <h2 className={styles.title}>Corporate Announcements</h2>
                </div>

                <div className={styles.filterRow} style={{ alignItems: 'flex-end' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label className={styles.filterLabel} style={{ fontSize: '12px', fontWeight: 'bold' }}>Company</label>
                        <Input
                            value={titleFilter}
                            onChange={(e) => { setPage(1); setTitleFilter(e.target.value); }}
                            placeholder="Search Company"
                            style={{ width: '160px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label className={styles.filterLabel} style={{ fontSize: '12px', fontWeight: 'bold' }}>Subject</label>
                        <Input
                            value={subjectFilter}
                            onChange={(e) => { setPage(1); setSubjectFilter(e.target.value); }}
                            placeholder="Search Subject"
                            style={{ width: '160px' }}
                        />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label className={styles.filterLabel} style={{ fontSize: '12px', fontWeight: 'bold' }}>Status</label>
                        <div style={{ width: '140px' }}>
                            <Select
                                value={statusFilter === 'true' ? { value: 'true', label: 'Published' } : statusFilter === 'false' ? { value: 'false', label: 'Not Published' } : null}
                                onChange={(selected) => { setPage(1); setStatusFilter(selected ? selected.value : ''); }}
                                options={[
                                    { value: '', label: 'All Status' },
                                    { value: 'true', label: 'Published' },
                                    { value: 'false', label: 'Not Published' },
                                ]}
                                placeholder="Select Status"
                                isClearable={false}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                components={{ IndicatorSeparator: () => null, DropdownIndicator: null }}
                                styles={{
                                    control: (base) => ({ ...base, minHeight: '5px' }),
                                    menu: (base) => ({ ...base, zIndex: 9999 }),
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 })
                                }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <label className={styles.filterLabel} style={{ fontSize: '12px', fontWeight: 'bold' }}>Date Range</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <Input
                                type="date"
                                value={dateFrom || ''}
                                onChange={(e) => { setPage(1); setDateFrom(e.target.value); }}
                                placeholder="From"
                                style={{ width: '110px' }}
                            />
                            <Input
                                type="date"
                                value={dateTo || ''}
                                onChange={(e) => { setPage(1); setDateTo(e.target.value); }}
                                placeholder="To"
                                style={{ width: '110px' }}
                            />
                        </div>
                    </div>

                    <Button onClick={() => { setPage(1); fetchAnnouncements(); }}>
                        Search
                    </Button>
                    <Button variant="secondary" onClick={handleClearFilters}>
                        Clear
                    </Button>
                </div>
            </div>

            <DataTable columns={columns} data={announcements} />

            {/* Pagination */}
            <div className={styles.imTableFooter}>
                <span>Showing {announcements.length} records</span>

                {totalPages > 1 && (
                    <div className={styles.imPagination}>
                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {"<"}
                        </button>

                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => {
                            if (
                                p === 1 ||
                                p === totalPages ||
                                (p >= page - 1 && p <= page + 1)
                            ) {
                                return (
                                    <button
                                        type="button"
                                        key={p}
                                        className={`${styles.imPageBtn} ${page === p ? styles.active : ""}`}
                                        onClick={() => setPage(p)}
                                    >
                                        {p}
                                    </button>
                                );
                            }

                            if (p === page - 2 || p === page + 2) {
                                return (
                                    <span key={p} style={{ color: "var(--text-muted)", padding: "0 4px" }}>
                                        ...
                                    </span>
                                );
                            }

                            return null;
                        })}

                        <button
                            type="button"
                            className={styles.imPageBtn}
                            disabled={page === totalPages}
                            onClick={() => setPage(page + 1)}
                        >
                            {">"}
                        </button>
                    </div>
                )}

                <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                    Page {page} of {totalPages}
                </span>
            </div>

            {isFormOpen && (
                <FullscreenModal
                    isOpen={isFormOpen}
                    onClose={() => setIsFormOpen(false)}
                    title="Publish Corporate Announcement as News"
                    maxWidth="910px"
                >
                    <CorporateAnnouncementForm
                        announcementId={selectedAnnouncementId}
                        onSubmitted={() => {
                            setIsFormOpen(false);
                            fetchAnnouncements();
                        }}
                        onCancel={() => setIsFormOpen(false)}
                    />
                </FullscreenModal>
            )}
        </>
    );
};

export default CorporateAnnouncementsTab;
